import { DestinationEvent } from '@eventbrite/event-renderer';
import { useExperiment } from '@eventbrite/statsig';
import { $FixMe } from '@eventbrite/ts-utils';
import * as Sentry from '@sentry/browser';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { OrganicEventSearchFiltersApiPayload } from '../../api/searchOrganicEvents';
import { search as searchApi, SearchApiResponse } from '../../redux/api/search';
import { EventSearch } from '../../types';
import {
    parseSearchDataToEvents,
    SearchResponseEvents,
} from '../../utils/transformation';

export type SearchDataParams = {
    eventSearch: EventSearch;
    searchContext: {
        experimentOverride: Record<string, $FixMe>;
        isStaff: boolean;
    };
    onNewData: (data: SearchCachedData) => $FixMe;
};

export type UseSearchDataResponse = SearchResponseEvents & {
    isLoading: boolean;
    error: unknown;
};

export type SearchCachedData = SearchResponseEvents &
    Pick<SearchApiResponse, 'metrics'>;

export function useSearchData({
    eventSearch,
    searchContext,
    onNewData,
}: SearchDataParams): UseSearchDataResponse {
    const useGenericEndpoint = useGenericEndpointExperiment();
    const {
        data = placeholderSearchData,
        isFetching,
        error,
    } = useQuery({
        queryKey: getEventSearchQueryKey(eventSearch),
        queryFn: () =>
            searchApi({
                searchQuery: eventSearch,
                ...searchContext,
                useGenericEndpoint,
            }),
        // Mimic previous behaviour
        staleTime: 3 * 60 * 1000,
        onError: (e: unknown) => handleSearchError(e, { eventSearch }),
        select: (apiData) => ({
            ...parseSearchDataToEvents(apiData.data),
            metrics: apiData.metrics,
        }),
    });

    useEffect(() => {
        // Wait until fetching is done
        if (isFetching || !data) {
            return;
        }

        onNewData(data);
    }, [onNewData, isFetching, data]);

    return {
        events: data.events,
        pagination: data.pagination,
        isLoading: isFetching,
        error,
    };
}

function useGenericEndpointExperiment() {
    const { value } = useExperiment(
        'homepage_search_ads_from_generic_endpoint',
        'loadFromGenericEndpoint',
        false,
    );
    return value;
}

const placeholderSearchData: SearchCachedData = {
    events: {
        organic: [],
        topMatches: [],
        promoted: [],
    },
    pagination: {
        page: 1,
        pageSize: 0,
        pageCount: 1,
        objectCount: 0,
        continuation: '',
    },
};

export function getEventSearchQueryKey(
    eventSearch: OrganicEventSearchFiltersApiPayload,
) {
    // Queries are hashed, so object reference is not important
    return ['search', eventSearch];
}

export function useCurrentSearchOrganicEvents(
    eventSearch: EventSearch,
): DestinationEvent[] {
    const queryClient = useQueryClient();
    const events = queryClient.getQueryData<SearchApiResponse>(
        getEventSearchQueryKey(eventSearch),
    );
    return events?.data.events.results || [];
}

function handleSearchError(
    e: unknown,
    { eventSearch }: Pick<SearchDataParams, 'eventSearch'>,
) {
    const isNetworkError =
        typeof Response !== 'undefined' && e instanceof Response;

    if (isNetworkError) {
        if (e.status < 500) {
            reportNetworkError(e.statusText, {
                search_body: eventSearch,
                status_code: e.status,
            });
        }
    } else {
        reportNetworkError(e, { search_body: eventSearch });
    }
}

function reportNetworkError(title: unknown, context: Record<string, unknown>) {
    Sentry.captureException(title, {
        extra: context,
    });
}
